import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function TableRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const isStriped = element.variant === "striped"

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      dispatchEvent(element.id, "rowClick", { rowIndex })
    },
    [element.id, dispatchEvent],
  )

  const rows = element.children ?? []
  const headerRow = rows[0]
  const dataRows = rows.slice(1)

  return (
    <div
      className={cn("w-full overflow-auto", element.className)}
      data-testid={`table-${element.id}`}
    >
      <table className="w-full caption-bottom text-sm">
        {headerRow && (
          <thead className="[&_tr]:border-b">
            <tr className="border-b transition-colors hover:bg-muted/50">
              {headerRow.children?.map((cell) => {
                const text = cell.text
                  ? String(resolveSymbol(cell.text) ?? "")
                  : undefined

                return (
                  <th
                    key={cell.id}
                    className="h-10 px-4 text-left align-middle font-medium text-muted-foreground"
                  >
                    {text ?? (
                      cell.children?.map((child) => (
                        <ElementRenderer key={child.id} element={child} />
                      ))
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
        )}
        <tbody className="[&_tr:last-child]:border-0">
          {dataRows.map((row, rowIndex) => (
            <tr
              key={row.id}
              className={cn(
                "border-b transition-colors hover:bg-muted/50 cursor-pointer",
                isStriped && rowIndex % 2 === 1 && "bg-muted/50",
              )}
              onClick={() => handleRowClick(rowIndex)}
            >
              {row.children?.map((cell) => {
                const text = cell.text
                  ? String(resolveSymbol(cell.text) ?? "")
                  : undefined

                return (
                  <td key={cell.id} className="p-4 align-middle">
                    {text ?? (
                      cell.children?.map((child) => (
                        <ElementRenderer key={child.id} element={child} />
                      ))
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
